/**
 * Contract Generation AI
 * Uses Groq to generate and enhance legal contracts
 */

import { getCountryContextForDocGeneration } from "@/shared/config/country-legal-context";
import { getGroq, GROQ_MODELS } from "./groq-client";

export interface ContractGenerationParams {
  templateType: string;
  contractData: Record<string, string>;
  country: string;
  additionalClauses?: string[];
  customInstructions?: string;
}

export interface GeneratedContract {
  content: string;
  sections: ContractSection[];
  warnings: string[];
  legalNotes: string[];
}

export interface ContractSection {
  title: string;
  content: string;
  order: number;
}

function stripGeneratedNotes(content: string): string {
  return content
    .replace(
      /\n*\s*(NOTA|NOTE)\s*:\s*Este documento ha sido generado con asistencia de inteligencia artificial\.[\s\S]*$/i,
      "",
    )
    .trim();
}

// Contract templates
export const CONTRACT_TEMPLATES = {
  employment: {
    name: "Contrato de Trabajo",
    nameEn: "Employment Contract",
    fields: [
      { key: "employer_name", label: "Nombre del Empleador", required: true },
      {
        key: "employer_address",
        label: "Dirección del Empleador",
        required: true,
      },
      { key: "employer_rfc", label: "RFC del Empleador", required: false },
      { key: "employee_name", label: "Nombre del Empleado", required: true },
      {
        key: "employee_address",
        label: "Dirección del Empleado",
        required: true,
      },
      {
        key: "employee_id",
        label: "Identificación del Empleado",
        required: false,
      },
      { key: "position", label: "Puesto de Trabajo", required: true },
      { key: "department", label: "Departamento", required: false },
      { key: "salary", label: "Salario Mensual", required: true },
      { key: "start_date", label: "Fecha de Inicio", required: true },
      { key: "work_schedule", label: "Horario de Trabajo", required: true },
      {
        key: "contract_duration",
        label: "Duración (indefinido o fecha fin)",
        required: true,
      },
      { key: "vacation_days", label: "Días de Vacaciones", required: false },
      { key: "benefits", label: "Prestaciones", required: false },
    ],
  },
  service: {
    name: "Contrato de Prestación de Servicios",
    nameEn: "Service Agreement",
    fields: [
      { key: "provider_name", label: "Nombre del Prestador", required: true },
      {
        key: "provider_address",
        label: "Dirección del Prestador",
        required: true,
      },
      { key: "provider_rfc", label: "RFC del Prestador", required: false },
      { key: "client_name", label: "Nombre del Cliente", required: true },
      { key: "client_address", label: "Dirección del Cliente", required: true },
      {
        key: "services_description",
        label: "Descripción de Servicios",
        required: true,
      },
      { key: "deliverables", label: "Entregables", required: true },
      { key: "fee_amount", label: "Monto del Honorario", required: true },
      { key: "payment_terms", label: "Términos de Pago", required: true },
      { key: "start_date", label: "Fecha de Inicio", required: true },
      { key: "end_date", label: "Fecha de Término", required: false },
    ],
  },
  nda: {
    name: "Acuerdo de Confidencialidad (NDA)",
    nameEn: "Non-Disclosure Agreement",
    fields: [
      { key: "disclosing_party", label: "Parte que Divulga", required: true },
      { key: "receiving_party", label: "Parte Receptora", required: true },
      { key: "purpose", label: "Propósito del Acuerdo", required: true },
      {
        key: "confidential_info_description",
        label: "Descripción de Información Confidencial",
        required: true,
      },
      {
        key: "duration",
        label: "Duración de la Confidencialidad",
        required: true,
      },
      { key: "jurisdiction", label: "Jurisdicción", required: true },
    ],
  },
  rental: {
    name: "Contrato de Arrendamiento",
    nameEn: "Rental Agreement",
    fields: [
      { key: "landlord_name", label: "Nombre del Arrendador", required: true },
      {
        key: "landlord_address",
        label: "Dirección del Arrendador",
        required: true,
      },
      { key: "tenant_name", label: "Nombre del Arrendatario", required: true },
      {
        key: "tenant_address",
        label: "Dirección Actual del Arrendatario",
        required: true,
      },
      {
        key: "property_address",
        label: "Dirección del Inmueble",
        required: true,
      },
      {
        key: "property_description",
        label: "Descripción del Inmueble",
        required: true,
      },
      { key: "monthly_rent", label: "Renta Mensual", required: true },
      {
        key: "security_deposit",
        label: "Depósito de Garantía",
        required: true,
      },
      { key: "payment_day", label: "Día de Pago", required: true },
      { key: "start_date", label: "Fecha de Inicio", required: true },
      {
        key: "contract_duration",
        label: "Duración del Contrato",
        required: true,
      },
      {
        key: "utilities_included",
        label: "Servicios Incluidos",
        required: false,
      },
    ],
  },
  sale: {
    name: "Contrato de Compraventa",
    nameEn: "Sale Contract",
    fields: [
      { key: "seller_name", label: "Nombre del Vendedor", required: true },
      {
        key: "seller_address",
        label: "Dirección del Vendedor",
        required: true,
      },
      { key: "buyer_name", label: "Nombre del Comprador", required: true },
      {
        key: "buyer_address",
        label: "Dirección del Comprador",
        required: true,
      },
      {
        key: "item_description",
        label: "Descripción del Bien",
        required: true,
      },
      { key: "sale_price", label: "Precio de Venta", required: true },
      { key: "payment_method", label: "Forma de Pago", required: true },
      { key: "delivery_date", label: "Fecha de Entrega", required: true },
      { key: "warranty", label: "Garantía", required: false },
    ],
  },
};

export type ContractTemplateType = keyof typeof CONTRACT_TEMPLATES;

/**
 * Generate a legal contract using AI
 */
export async function generateContract(
  params: ContractGenerationParams
): Promise<GeneratedContract> {
  const groq = getGroq();
  const {
    templateType,
    contractData,
    country,
    additionalClauses,
    customInstructions,
  } = params;

  const template = CONTRACT_TEMPLATES[templateType as ContractTemplateType];
  if (!template) {
    throw new Error(`Template type '${templateType}' not found`);
  }

  const countryContext =
    getCountryContextForDocGeneration(country).split(",")[0] || "Latinoamérica";

  const systemPrompt = `Eres un abogado experto en ${countryContext} especializado en redacción de contratos.
Tu tarea es generar un contrato legal profesional, completo y válido según las leyes de ${countryContext}.

INSTRUCCIONES:
1. Genera un contrato formal y profesional
2. Incluye todas las cláusulas estándar necesarias
3. Usa terminología legal apropiada para ${countryContext}
4. El contrato debe ser legalmente vinculante
5. Incluye espacios para firmas al final
6. Añade advertencias legales si es necesario
7. Devuelve únicamente el contrato legal final, sin notas o disclaimers fuera del cuerpo del documento

TIPO DE CONTRATO: ${template.name}

${customInstructions ? `INSTRUCCIONES ADICIONALES: ${customInstructions}` : ""}

FORMATO DE RESPUESTA:
Debes responder ÚNICAMENTE con un objeto JSON válido (sin markdown) con la siguiente estructura:
{
  "content": "<contrato completo en texto formateado>",
  "sections": [
    { "title": "Título de Sección", "content": "Contenido de la sección", "order": 1 }
  ],
  "warnings": ["Advertencia legal 1", "Advertencia 2"],
  "legalNotes": ["Nota legal 1", "Nota 2"]
}`;

  const dataText = Object.entries(contractData)
    .filter(([_, value]) => value && value.trim())
    .map(([key, value]) => {
      const field = template.fields.find((f) => f.key === key);
      return `- ${field?.label || key}: ${value}`;
    })
    .join("\n");

  const userPrompt = `Genera un ${template.name} con los siguientes datos:

DATOS DEL CONTRATO:
${dataText}

${additionalClauses && additionalClauses.length > 0 ? `\nCLÁUSULAS ADICIONALES:\n${additionalClauses.join("\n")}` : ""}

Genera el contrato completo en formato JSON.`;

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODELS.DOCUMENTS, // Best quality for document generation
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.3,
      max_tokens: 4000,
    });

    const content = completion.choices[0]?.message?.content;

    if (!content) {
      throw new Error("No content generated");
    }

    // Parse JSON response
    const cleanedContent = content
      .trim()
      .replace(/^```json\s*/i, "")
      .replace(/^```\s*/, "")
      .replace(/```\s*$/, "");

    const result = JSON.parse(cleanedContent);

    return {
      content: result.content || "",
      sections: Array.isArray(result.sections) ? result.sections : [],
      warnings: Array.isArray(result.warnings) ? result.warnings : [],
      legalNotes: Array.isArray(result.legalNotes) ? result.legalNotes : [],
    };
  } catch (error: any) {
    console.error("Error generating contract:", error);
    throw new Error(`Error al generar el contrato: ${error.message}`);
  }
}

/**
 * =========================================
 * ENHANCED DOCUMENT GENERATION WITH DATABASE REQUIREMENTS
 * =========================================
 */

import {
  getDocumentRequirements,
  type DocumentRequirements,
} from "../db/queries/documentRequirements";
import { getCountry, type CountryConfig } from "../config/countries";
import {
  getDocumentTypeName,
  getDocumentTypeDescription,
} from "../config/document-types";

export interface EnhancedGenerationParams {
  documentType: string;
  countryCode: string;
  formData: Record<string, string>;
  language: "es" | "en";
}

export interface EnhancedGeneratedDocument {
  content: string;
  title: string;
  warnings: string[];
  legalNotes: string[];
  requirements: DocumentRequirements | null;
  country: CountryConfig | null;
  complianceCheck: {
    coversClauses: boolean;
    missingClauses: string[];
    usesCorrectCurrency: boolean;
    referencesLaws: boolean;
  };
  status: "draft" | "pending_review";
}

/**
 * Generate a legal document using AI with full database integration
 * Uses document_requirements to ensure legal compliance
 */
export async function generateEnhancedDocument(
  params: EnhancedGenerationParams
): Promise<EnhancedGeneratedDocument> {
  const { documentType, countryCode, formData, language } = params;
  const groq = getGroq();

  // Load country config from database
  const country = await getCountry(countryCode);
  if (!country) {
    throw new Error(`Country not found: ${countryCode}`);
  }

  // Load document requirements from database
  const requirements = await getDocumentRequirements(countryCode, documentType);

  // Get document type info
  const docTypeName = getDocumentTypeName(documentType, language);
  const docTypeDesc = getDocumentTypeDescription(documentType, language);

  const isSpanish = language === "es";
  const countryName = isSpanish ? country.nameEs : country.nameEn;
  const legalSystem = isSpanish ? country.legalSystemEs : country.legalSystemEn;
  const currencyName = isSpanish
    ? country.currencyNameEs
    : country.currencyNameEn;

  // Build legal context from database
  let legalContext = "";
  if (requirements) {
    const clauses = isSpanish
      ? requirements.requiredClauses.es
      : requirements.requiredClauses.en;
    const formatDesc = isSpanish
      ? requirements.formatDescriptionEs
      : requirements.formatDescriptionEn;

    legalContext = isSpanish
      ? `
**REQUISITOS LEGALES OBLIGATORIOS (DE LA BASE DE DATOS):**

Las siguientes cláusulas DEBEN estar presentes en el documento según la legislación de ${countryName}:
${clauses.map((c, i) => `${i + 1}. ${c}`).join("\n")}

**Marcos Legales Aplicables que deben mencionarse:**
${requirements.legalFrameworkCodes.join(", ")}

**Formato Requerido:**
${formatDesc}
`
      : `
**MANDATORY LEGAL REQUIREMENTS (FROM DATABASE):**

The following clauses MUST be present in the document according to ${countryName} legislation:
${clauses.map((c, i) => `${i + 1}. ${c}`).join("\n")}

**Applicable Legal Frameworks to be mentioned:**
${requirements.legalFrameworkCodes.join(", ")}

**Required Format:**
${formatDesc}
`;
  }

  // Format form data
  const formDataText = Object.entries(formData)
    .filter(([_, value]) => value && value.toString().trim())
    .map(([key, value]) => `- ${key}: ${value}`)
    .join("\n");

  const systemPrompt = isSpanish
    ? `Eres un abogado experto en derecho de ${countryName} especializado en redacción de documentos legales.

SISTEMA LEGAL: ${legalSystem}
MONEDA OFICIAL: ${currencyName} (${country.currencyCode})

INSTRUCCIONES CRÍTICAS:
1. Genera un documento legal COMPLETO, profesional y válido según las leyes de ${countryName}
2. DEBES incluir TODAS las cláusulas requeridas listadas más abajo
3. Usa la moneda correcta: ${currencyName} (${country.currencyCode})
4. Referencia las leyes aplicables cuando corresponda
5. Incluye fecha, lugar, y espacios para firmas
6. El documento debe ser legalmente vinculante en ${countryName}
7. Usa numeración para las cláusulas

${legalContext}

FORMATO DE RESPUESTA (JSON):
{
  "title": "Título oficial del documento",
  "content": "Documento completo en texto formateado con cláusulas numeradas",
  "warnings": ["Advertencias legales importantes"],
  "legalNotes": ["Notas sobre cumplimiento legal"],
  "referencedLaws": ["Lista de leyes referenciadas en el documento"]
}`
    : `You are an expert lawyer specializing in ${countryName} law and legal document drafting.

LEGAL SYSTEM: ${legalSystem}
OFFICIAL CURRENCY: ${currencyName} (${country.currencyCode})

CRITICAL INSTRUCTIONS:
1. Generate a COMPLETE, professional and valid legal document according to ${countryName} laws
2. You MUST include ALL required clauses listed below
3. Use the correct currency: ${currencyName} (${country.currencyCode})
4. Reference applicable laws when appropriate
5. Include date, place, and signature spaces
6. The document must be legally binding in ${countryName}
7. Use numbering for clauses
8. Return only the final legal document text, without extra notes or disclaimers outside the document body

${legalContext}

RESPONSE FORMAT (JSON):
{
  "title": "Official document title",
  "content": "Complete document in formatted text with numbered clauses",
  "warnings": ["Important legal warnings"],
  "legalNotes": ["Notes on legal compliance"],
  "referencedLaws": ["List of laws referenced in the document"]
}`;

  const userPrompt = isSpanish
    ? `Genera un ${docTypeName} para ${countryName} con los siguientes datos:

DATOS DEL DOCUMENTO:
${formDataText}

DESCRIPCIÓN DEL TIPO DE DOCUMENTO:
${docTypeDesc || docTypeName}

Genera el documento completo en formato JSON. El documento debe ser profesional, completo y legalmente válido en ${countryName}.`
    : `Generate a ${docTypeName} for ${countryName} with the following data:

DOCUMENT DATA:
${formDataText}

DOCUMENT TYPE DESCRIPTION:
${docTypeDesc || docTypeName}

Generate the complete document in JSON format. The document must be professional, complete and legally valid in ${countryName}.`;

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODELS.DOCUMENTS,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.3,
      max_tokens: 6000,
      response_format: { type: "json_object" },
    });

    const content = completion.choices[0]?.message?.content;
    if (!content) {
      throw new Error("No content generated");
    }

    const result = JSON.parse(content);
    let docContent = stripGeneratedNotes(result.content || "");

    // Keep output as pure legal document content (no appended notes/disclaimers)
    docContent = docContent.trim();

    // Perform compliance check
    const complianceCheck = requirements
      ? checkCompliance(
          docContent,
          requirements,
          language,
          country.currencyCode
        )
      : {
          coversClauses: true,
          missingClauses: [],
          usesCorrectCurrency: true,
          referencesLaws: true,
        };

    return {
      content: docContent,
      title: result.title || docTypeName,
      warnings: Array.isArray(result.warnings) ? result.warnings : [],
      legalNotes: Array.isArray(result.legalNotes) ? result.legalNotes : [],
      requirements,
      country,
      complianceCheck,
      status: "pending_review",
    };
  } catch (error: any) {
    console.error("Error generating enhanced document:", error);
    throw new Error(`Error al generar el documento: ${error.message}`);
  }
}

/**
 * Check if generated document complies with requirements
 */
function checkCompliance(
  content: string,
  requirements: DocumentRequirements,
  language: "es" | "en",
  currencyCode: string
): {
  coversClauses: boolean;
  missingClauses: string[];
  usesCorrectCurrency: boolean;
  referencesLaws: boolean;
} {
  const contentLower = content.toLowerCase();
  const clauses =
    language === "es"
      ? requirements.requiredClauses.es
      : requirements.requiredClauses.en;

  // Check which clauses might be missing (simplified check)
  const missingClauses: string[] = [];
  clauses.forEach((clause) => {
    // Extract key words from clause for checking
    const keywords = clause
      .toLowerCase()
      .split(" ")
      .filter((w) => w.length > 4);
    const matchCount = keywords.filter((kw) =>
      contentLower.includes(kw)
    ).length;
    // If less than 30% of keywords found, consider it potentially missing
    if (matchCount / keywords.length < 0.3) {
      missingClauses.push(clause);
    }
  });

  // Check currency
  const usesCorrectCurrency =
    contentLower.includes(currencyCode.toLowerCase()) ||
    contentLower.includes(currencyCode);

  // Check law references
  const referencesLaws = requirements.legalFrameworkCodes.some(
    (code) =>
      contentLower.includes(code.toLowerCase().replace(/_/g, " ")) ||
      contentLower.includes("ley") ||
      contentLower.includes("código") ||
      contentLower.includes("law") ||
      contentLower.includes("code")
  );

  return {
    coversClauses: missingClauses.length === 0,
    missingClauses,
    usesCorrectCurrency,
    referencesLaws,
  };
}

/**
 * Improve a generated document based on feedback (Enhanced version with requirements)
 */
export async function improveDocumentWithRequirements(
  originalContent: string,
  feedback: string,
  requirements: DocumentRequirements | null,
  language: "es" | "en"
): Promise<string> {
  const groq = getGroq();

  const isSpanish = language === "es";

  const systemPrompt = isSpanish
    ? `Eres un abogado experto. Tu tarea es mejorar el documento legal proporcionado basándote en el feedback del usuario. Mantén la estructura legal y el formato del documento original, pero aplica las mejoras solicitadas.`
    : `You are an expert lawyer. Your task is to improve the provided legal document based on user feedback. Maintain the legal structure and format of the original document, but apply the requested improvements.`;

  const userPrompt = isSpanish
    ? `DOCUMENTO ORIGINAL:
${originalContent}

FEEDBACK PARA MEJORAR:
${feedback}

${
  requirements
    ? `
REQUISITOS LEGALES A MANTENER:
${requirements.requiredClauses.es.join("\n")}
`
    : ""
}

Genera el documento mejorado completo.`
    : `ORIGINAL DOCUMENT:
${originalContent}

FEEDBACK TO IMPROVE:
${feedback}

${
  requirements
    ? `
LEGAL REQUIREMENTS TO MAINTAIN:
${requirements.requiredClauses.en.join("\n")}
`
    : ""
}

Generate the complete improved document.`;

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODELS.DOCUMENTS,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.4,
      max_tokens: 6000,
    });

    const improved = completion.choices[0]?.message?.content || originalContent;
    return stripGeneratedNotes(improved);
  } catch (error: any) {
    console.error("Error improving document:", error);
    throw new Error(`Error al mejorar el documento: ${error.message}`);
  }
}
